/**
 * Freight / LVO access rules of the connected user.
 *
 * FRET_STATISTIC (their agency) and GLOBAL_FRET_STATISTIC (every agency) are
 * read-only profiles: on the freight declaration and waybill lists they may
 * only filter and export, and on the dashboard they only see the freight / LVO
 * statistics. The backend applies the same rules (scope, export rights, 403 on
 * any write).
 */

import { useMemo } from 'react';
import { useAuth } from '../store/AuthContext';

export const FREIGHT_STATISTIC_PROFILES = ['FRET_STATISTIC', 'GLOBAL_FRET_STATISTIC'];

/**
 * Profiles that write on a freight, its lots, its payments or its LVO. A freight
 * statistics user holding none of them is read-only (same list as the API).
 */
const FREIGHT_WRITE_PROFILES = [
  'ADMIN',
  'AGENCY_HEAD',
  'MAKE_FREIGHT_DECLARATION',
  'MAKE_FREIGHT_CALIBRATION',
  'FREIGHT_MANAGER',
  'FREIGHT_AGENT',
  'AMEND_LV',
  'PRINT_LV',
  'CASHIER',
  'DOCUMENT_MANAGER',
  'DOCUMENT_AGENT',
];

/**
 * Profiles that keep the main dashboard figures next to a freight statistics
 * profile: without any of them, FRET_STATISTIC / GLOBAL_FRET_STATISTIC only see
 * the freight / LVO statistics.
 */
const MAIN_STATISTICS_PROFILES = [
  'STATISTIC',
  'ACCOUNTING_OFFICER',
  'GLOBAL_ACCOUNTING_OFFICER',
  ...FREIGHT_WRITE_PROFILES,
];

/** Profiles that open the freight / LVO statistics section of the dashboard. */
const FREIGHT_STATS_PROFILES = [
  'ADMIN',
  'STATISTIC',
  'AGENCY_HEAD',
  'ACCOUNTING_OFFICER',
  'GLOBAL_ACCOUNTING_OFFICER',
  'FREIGHT_MANAGER',
  'FREIGHT_AGENT',
  'MAKE_FREIGHT_DECLARATION',
  'MAKE_FREIGHT_CALIBRATION',
  ...FREIGHT_STATISTIC_PROFILES,
];

export interface FreightAccess {
  /** Holds FRET_STATISTIC or GLOBAL_FRET_STATISTIC. */
  hasFreightStatistic: boolean;
  /** Holds GLOBAL_FRET_STATISTIC. */
  isGlobalFreightStatistic: boolean;
  /** A freight statistics profile and no freight writing profile: filters and export only. */
  isReadOnly: boolean;
  /** Freight statistics profile without any profile of the main dashboard: the other figures stay hidden. */
  freightStatisticsOnly: boolean;
  /** The freight / LVO statistics section of the dashboard is shown. */
  canSeeFreightStatistics: boolean;
  /** Freight declarations export (ADMIN, AGENCY_HEAD, FRET_STATISTIC, GLOBAL_FRET_STATISTIC). */
  canExportFreights: boolean;
  /** Agency the freight export is restricted to (AGENCY_HEAD and FRET_STATISTIC). */
  freightExportAgencyId: number | undefined;
  /** The agency filter of the freight declaration list is shown. */
  showFreightAgencyFilter: boolean;
}

export function useFreightAccess(): FreightAccess {
  const { user } = useAuth();

  return useMemo(() => {
    const profiles = user?.profiles ?? [];
    const has = (code: string) => profiles.includes(code);
    const hasFreightStatistic = FREIGHT_STATISTIC_PROFILES.some(has);
    const isGlobalFreightStatistic = has('GLOBAL_FRET_STATISTIC');
    const isAdmin = has('ADMIN');
    const canExportFreights =
      isAdmin || has('AGENCY_HEAD') || hasFreightStatistic;
    const globalExport = isAdmin || isGlobalFreightStatistic;

    return {
      hasFreightStatistic,
      isGlobalFreightStatistic,
      isReadOnly: hasFreightStatistic && !profiles.some((p) => FREIGHT_WRITE_PROFILES.includes(p)),
      freightStatisticsOnly:
        hasFreightStatistic && !profiles.some((p) => MAIN_STATISTICS_PROFILES.includes(p)),
      canSeeFreightStatistics:
        user?.structure_type !== 'CARRIER' &&
        user?.user_type !== 'DRIVER' &&
        FREIGHT_STATS_PROFILES.some(has),
      canExportFreights,
      freightExportAgencyId:
        canExportFreights && !globalExport ? user?.agency_id ?? undefined : undefined,
      showFreightAgencyFilter: globalExport,
    };
  }, [user?.profiles, user?.agency_id, user?.structure_type, user?.user_type]);
}
